import type React from 'react';
import { useEffect, useState } from 'react';
import StacErrorBoundary from './StacErrorBoundary';
import AIAssistant from './pages/AIAssistant';
import FilingReview from './pages/FilingReview';
import NexusStatus from './pages/NexusStatus';
import ProductClassification from './pages/ProductClassification';
import StacSettings from './pages/StacSettings';
import TaxReturns from './pages/TaxReturns';

type ActivePage = 'nexus' | 'classification' | 'returns' | 'settings' | 'ai-assistant' | string;

interface FilingTarget {
  stateCode: string;
  stateName: string;
  period: string;
}

interface StacShellProps {
  activePage: ActivePage;
  organizationId: string;
}

// Pages whose data is worth keeping in memory across tab switches — they stay
// mounted (hidden via display:none) once visited instead of unmounting, so
// returning to a tab shows its already-fetched data instantly instead of a
// skeleton. Settings and AI Assistant are cheap/rarely revisited and keep the
// original mount-on-visit behavior.
type KeptAlivePage = 'nexus' | 'classification' | 'returns';

export default function StacShell({ activePage, organizationId }: StacShellProps): JSX.Element {
  const [filingTarget, setFilingTarget] = useState<FilingTarget | null>(null);
  const [visited, setVisited] = useState<Set<ActivePage>>(() => new Set([activePage]));

  useEffect(() => {
    if (activePage !== 'returns') setFilingTarget(null);
    setVisited((prev) => (prev.has(activePage) ? prev : new Set(prev).add(activePage)));
  }, [activePage]);

  const showFilingReview = activePage === 'returns' && filingTarget !== null;

  function displayStyle(page: KeptAlivePage): React.CSSProperties {
    const isActive = activePage === page && !(page === 'returns' && showFilingReview);
    return { display: isActive ? 'contents' : 'none' };
  }

  return (
    <>
      {visited.has('nexus') && (
        <div style={displayStyle('nexus')}>
          <StacErrorBoundary>
            <NexusStatus />
          </StacErrorBoundary>
        </div>
      )}
      {visited.has('classification') && (
        <div style={displayStyle('classification')}>
          <StacErrorBoundary>
            <ProductClassification />
          </StacErrorBoundary>
        </div>
      )}
      {visited.has('returns') && (
        <div style={displayStyle('returns')}>
          <StacErrorBoundary>
            <TaxReturns
              onReviewFiling={(stateCode, stateName, period) =>
                setFilingTarget({ stateCode, stateName, period })
              }
            />
          </StacErrorBoundary>
        </div>
      )}
      {showFilingReview && filingTarget && (
        <StacErrorBoundary>
          <FilingReview
            stateCode={filingTarget.stateCode}
            stateName={filingTarget.stateName}
            period={filingTarget.period}
            onBack={() => setFilingTarget(null)}
          />
        </StacErrorBoundary>
      )}
      {activePage === 'settings' && (
        <StacErrorBoundary>
          <StacSettings />
        </StacErrorBoundary>
      )}
      {activePage === 'ai-assistant' && (
        <StacErrorBoundary>
          <AIAssistant />
        </StacErrorBoundary>
      )}
    </>
  );
}
